"use client"

import React, {useState} from "react";
import {
  EventResponse, EventResponses,
  EventResponseStatus,
  eventResponseStatusSchema,
  EventWithResponses, GameEventWithResponses
} from "@vereinsheim/backend-types/src/event";
import {apiEventResponseUpsertPath} from "@/utils/urlHelper";
import {Person} from "@/sources/kicker-amateur/types";
import Button from "@/app/pwa/components/Button";
import CloseCircle from "@/assets/icon/close.svg";
import HelpCircle from "@/assets/icon/help.svg";
import CheckmarkCircle from "@/assets/icon/checkmark.svg";
import {useRouter} from "next/navigation";
import {tryParseResponseError} from "@/app/utils/tryParseResponseError";

function checkForPersonResponse(event:EventWithResponses|GameEventWithResponses, personId?:number):EventResponseStatus|undefined{
  if(personId === undefined){ return undefined }
  return event.responses[personId]
}

interface EventCardActionsProps {
  event: EventWithResponses|GameEventWithResponses
  person?: Person
  isMultiPerson?: boolean
  onResponseChange?: (response: EventResponse) => void
}

enum LoadingState {
  Accepting,
  Declining,
  Maybe,
  NoLoading
}

export function EventCardActions({ event, person, isMultiPerson, onResponseChange}: EventCardActionsProps) {

  const [loading, setLoading] = useState<LoadingState>(LoadingState.NoLoading);
  const [backendError, setBackendError] = useState<string | null>(null);
  const router = useRouter()
  const initialStatus = checkForPersonResponse(event,person?.Id)
  const defaultStatus=
    event.responseMode === "default-accepted" ?
      eventResponseStatusSchema.enum.accepted
      :
      (event.responseMode === "default-declined" ?
          eventResponseStatusSchema.enum.declined
          :
          (event.responseMode === "default-unknown" ? eventResponseStatusSchema.enum.maybe : undefined)
      )
  const [currentStatus, setCurrentStatus] = useState<EventResponseStatus|undefined>(initialStatus ?? defaultStatus)

  async function handleResponse(responseType: EventResponseStatus) {
    if(!person){
      return
    }
    const data: EventResponse = {
      eventId: event._id,
      personId: `${person.Id}`,
      status: responseType
    }
    setLoading(responseType == 'accepted' ? LoadingState.Accepting :
      (responseType == 'declined' ? LoadingState.Declining :
        (responseType == 'maybe' ? LoadingState.Maybe : LoadingState.NoLoading)
      )
    );
    const res = await fetch(apiEventResponseUpsertPath(), {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(data),
    });
    setLoading(LoadingState.NoLoading);
    if (res.ok) {
      setCurrentStatus(responseType)
      if(person.Id) {
        if(onResponseChange) {
          onResponseChange(await res.json())
        }
      }
      router.refresh()
    } else {
      const errorMessage = await tryParseResponseError(res);
      setBackendError(errorMessage);
    }
  }

  const roundedT = isMultiPerson ? "rounded-t-none" : "";

  const auto = !initialStatus && currentStatus === defaultStatus

  const accepted = currentStatus === eventResponseStatusSchema.enum.accepted;
  const acceptedAuto = accepted && auto;

  const maybe = currentStatus === eventResponseStatusSchema.enum.maybe;
  const maybeAuto =  maybe && auto;

  const declined = currentStatus === eventResponseStatusSchema.enum.declined;
  const declinedAuto = declined && auto

  return (
    <div className="">
    {currentStatus && <div className="flex gap-x-0.5">
      { accepted &&
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.accepted)}
        className={`text-accepted font-semibold w-2/3 flex justify-center hover:z-10 ${roundedT} bg-gray-100 rounded-r-none relative`}
        color="link-dark"
        loading={loading == LoadingState.Accepting}
        disabled={loading == LoadingState.Declining || loading == LoadingState.Maybe }>
          <CheckmarkCircle className="mr-1.5 flex-shrink-0" />
          {accepted && acceptedAuto ? "Zugesagt" : "Zugesagt"}
          <div className="bg-current h-0.5 absolute bottom-0 left-0 right-0"></div>
        </Button>
      }
      { !(accepted || acceptedAuto) &&
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.accepted)}
                className={`text-accepted w-1/6 flex justify-center hover:z-10 ${roundedT} bg-gray-100 rounded-r-none relative`}
                color="link-dark"
                loading={loading == LoadingState.Accepting}
                disabled={loading == LoadingState.Declining || loading == LoadingState.Maybe }>
          <CheckmarkCircle />
        </Button>
      }


      { maybe &&
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.maybe)}
        className={`text-maybe font-semibold w-2/3 flex justify-center hover:z-10 ${roundedT} bg-gray-100 rounded-none relative`}
        color="link-muted"
        loading={loading == LoadingState.Maybe}
        disabled={loading == LoadingState.Declining || loading == LoadingState.Accepting }>
          <HelpCircle className="mr-1.5 flex-shrink-0" />
          {maybe && maybeAuto ? "Unsicher" : "Unsicher"}
          <div className="bg-current h-0.5 absolute bottom-0 left-0 right-0"></div>
        </Button>
      }
      { !(maybe || maybeAuto) &&
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.maybe)}
                className={`text-maybe w-1/6 flex justify-center hover:z-10 ${roundedT} bg-gray-100 rounded-none relative`}
                color="link-muted"
                loading={loading  == LoadingState.Maybe}
                disabled={loading == LoadingState.Declining || loading == LoadingState.Accepting }>
          <HelpCircle />
        </Button>
      }


      { declined &&
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.declined)}
        className={`text-declined font-semibold w-2/3 flex justify-center hover:z-10 ${roundedT} bg-gray-100 rounded-l-none relative`}
        color="link-dark"
        loading={loading == LoadingState.Declining}
        disabled={loading == LoadingState.Maybe || loading == LoadingState.Accepting }>
          <CloseCircle className="mr-1.5 flex-shrink-0" />
          {declined && declinedAuto ? "Abgesagt" : "Abgesagt"}
          <div className="bg-current h-0.5 absolute bottom-0 left-0 right-0"></div>
        </Button>
      }
      { !(declined || declinedAuto) &&
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.declined)}
                className={`text-declined w-1/6 flex justify-center hover:z-10 ${roundedT} bg-gray-100 rounded-l-none relative`}
                color="link-dark"
                loading={loading == LoadingState.Declining}
                disabled={loading == LoadingState.Maybe || loading == LoadingState.Accepting }>
          <CloseCircle />
        </Button>
      }
    </div>}

    {!currentStatus &&
      <div className="flex gap-x-0.5">
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.accepted)}
                className={`w-1/3 flex justify-center hover:z-10 rounded-r-none ${roundedT} relative`}
                disabled={loading == LoadingState.Declining || loading == LoadingState.Maybe }
                color="accepted"
                loading={loading == LoadingState.Accepting}>
          <CheckmarkCircle className="mr-1.5 flex-shrink-0" />Zusagen
        </Button>
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.maybe)}
                className={`w-1/3 flex justify-center hover:z-10 rounded-none ${roundedT} relative`}
                disabled={loading == LoadingState.Declining || loading == LoadingState.Accepting }
                color="maybe"
                loading={loading == LoadingState.Maybe}>
          <HelpCircle className="mr-1.5 flex-shrink-0" />Unsicher
        </Button>
        <Button onClick={() => handleResponse(eventResponseStatusSchema.enum.declined)}
                className={`w-1/3 flex justify-center hover:z-10 rounded-l-none ${roundedT} relative`}
                disabled={loading == LoadingState.Maybe || loading == LoadingState.Accepting}
                color="declined"
                loading={loading == LoadingState.Declining}>
          <CloseCircle className="mr-1.5 flex-shrink-0" />Absagen
        </Button>
      </div>
    }
   </div>
  )
}
